<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b>: it has the same columns.</li>
                     <li><b>Google Maps API key</b> is required.</li>
                     <li>Only one setting is active per company and branch: the <b>last row</b> of the file becomes the active setting, and all others become inactive.</li>
                     <li>If no row is valid, nothing is saved and your current key keeps working.</li>
                     <li>Settings are imported into the company and branch you are logged into. Switch to a specific branch first.</li>
                     <li>The file holds secret keys: keep it safe and delete it after importing.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

      
         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->



   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Google Map Settings' : 'Add Google Map Settings'"
      title-icon="bi bi-geo-alt"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: API Key -->
      <div class="row g-3 mb-0">
         <PasswordField
            v-model="formInputs.google_maps_api_key"
            col="col-md-12"
            name="google_maps_api_key"
            label="Google Maps API Key"
            icon="fa fa-key"
            autocomplete="off"
            :placeholder="isEditing ? 'Leave blank to keep the current one' : 'e.g. AIzaSy...'"
            :tooltip="isEditing ? 'Leave blank to keep the current key' : 'Paste the key from your Google Cloud account'"
            :required="!isEditing"
            @input="handleInputChange"
         />
      </div>

      <!-- Help link -->
      <div class="row g-3 mt-1">
         <div class="col-12">
            <small class="sa-core-color">
               <i class="fa fa-external-link me-1"></i>
               <a target="_blank" rel="noopener noreferrer" href="https://developers.google.com/maps/documentation/embed/get-api-key">How to create a Google Maps API key</a>
            </small>
         </div>
      </div>
   </RecordFormModal>
   

   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('google_map_settings.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->


         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
          <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('google_maps_api_key')" column="google_maps_api_key" :table-data="tableData" @sort="orderBy">API Key</SortableTh>
         <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
         <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date Created</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('google_maps_api_key')" class="text-break">
               <!-- Phones: + / − and the number go in front of the key (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span><code>{{ maskKey(record.google_maps_api_key_last4) }}</code>
            </td>
            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
               <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
               </span>
            </td>
            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('google_map_settings.delete')"
                  :can-restore="$hasPermission('google_map_settings.delete')"
                  :can-delete-permanently="$hasPermission('google_map_settings.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                   :modal-id="'deleteCrudModal' + record.id"
                   :title="deleteModalTitle"
                   :message="deleteModalMessage"
                   @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="5" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Setting Number:</strong> {{ record.google_map_setting_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('google_maps_api_key') }"><i class="fa fa-key"></i><strong>API Key:</strong> <code>{{ maskKey(record.google_maps_api_key_last4) }}</code></div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                        <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                        <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                        </span>
                     </div>
                     <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                     <!-- Always in details -->
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="7" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No Google Maps key yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Add your key so maps and locations work.'"
               icon="fa fa-map-marker"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="7">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>                    

         </div>

      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import PasswordField from "../../../components/admin/PasswordField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import googleMapSettingsMixin from "./mixins/googleMapSettingsMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'GoogleMapSettings',
   mixins: [tableMixin, googleMapSettingsMixin], // shared table methods first, then GoogleMapSettings' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      PasswordField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/settings/api_google_map_settings.php", // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "google_map_settings_import_template.xlsx",
         exportFileName: "googleMapSettingsExport", // name of the exported file
         columnsKey: "googlemapsettings",  // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         pageIcon: 'fa fa-map-marker', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "API Key", value: "google_maps_api_key" },
            { label: "Setting Number", value: "google_map_setting_number" },
         ],
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
      // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Google Map Settings",
              byme: "Google Map Settings By Me",
              deletedone: "Google Map Settings Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Google Map Settings";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "google_maps_api_key", label: "API Key" },
              { key: "is_active", label: "Status" },
              { key: "date_created", label: "Date Created" },
          ];
      },
  },
   methods: {
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            google_maps_api_key: ""
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'google_maps_api_key',
            searchPlaceHolderText: 'Search by API Key',
         };
      },
      // Show only the last 4 characters of a secret key
      maskKey(key) {
         if (!key) return "-";
         return "••••" + String(key).slice(-4);
      },
      addSampleData() {
         this.formInputs = {
            ...this.getDefaultFormInputs(),
            // Fake key in Google's format (39 chars, starts with "AIza"); maps won't load with it
            google_maps_api_key: "AIzaSyD-SAMPLE-KEY-0000000000000000000",
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      saveOrEditRecord() {      
      if (this.isSaving) return;
      this.isSaving = true;

      try {   
         this.$scrollToTopModal("recordFormModal"); 
         const formIsValid = this.$validateForm('#recordForm');
         this.submitted = true;
         if (!formIsValid) { 
          this.$playErrorSound();
                this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return;
        }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }

         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting",response.data.message[0],"danger","modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("google_maps_api_key", this.formInputs.google_maps_api_key);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.$clearValidationStates('#recordForm');
         this.formInputs.id = records.id ?? '';
         this.formInputs.google_maps_api_key = ''; // never sent to the browser: blank keeps the saved one
      },
   },
}; 
</script>